import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import MeasuredBy from "../molecules/MeasuredBy";

import { GENDERS } from "../../constants/modal";

import {
  disabledStylesContinue,
  activeStylesContinue,
  femaleBraScreenUI,
  welcomeScreenUI,
  font,
  uxShoeSizeRanges,
} from "../../configs";
import { shoeSizesWithinRanges } from "../../utils";
import StyledSelector from "./components/StyledSelector";
import BaseSizeSelector from "./components/BaseSizeSelector";
import { useMediaQuery } from "react-responsive";
import { findBrandByDomain } from "../../configs/configLoader";
import { hoverStylesContinue, titleStyles } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";

import "./index.css";

interface IPropsStepScanSelect {
  step: any;
  value_1: {
    current: string | null;
  };
  nextStep: () => void;
  sizeCountry: string;
  setSizeCountry: React.Dispatch<React.SetStateAction<string>>;
}

type BodyItemsType = "unfocused" | "focused";

// The brand's config is resolved before this module loads, so the grid is
// settled once rather than rebuilt on every render.
const configuredShoeSizes = shoeSizesWithinRanges(uxShoeSizeRanges);

const StepScanSelect: FC<IPropsStepScanSelect> = ({
  step,
  value_1,
  nextStep,
  sizeCountry,
  setSizeCountry,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { t } = useTranslation("components/size_select");

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [selectedSize, setSelectedSize] = useState<any | undefined>();

  const urlParameters = new URLSearchParams(window.location.search);
  const gender = urlParameters.get("gender");

  const brandDefined = findBrandByDomain();

  const isIntersport = brandDefined?.name === "Intersport";

  const fontFamily = `${font}, sans-serif`;

  useEffect(() => {
    if (selectedSize) {
      value_1.current = selectedSize ? selectedSize?.toString() : null;
    }
  }, [sizeCountry, selectedSize, value_1]);

  const getBodyItemsStyles = (type: BodyItemsType) => {
    const sizeSelectorItem = femaleBraScreenUI.sizeSelector;

    const isSizeUnderlined =
      (sizeSelectorItem["focused"] as any)?.textDecoration === "underline";

    const borderWidth = isSizeUnderlined
      ? "0 0 1px 0"
      : sizeSelectorItem[type].borderWidth;

    const bodyItemsStyles = {
      padding: "13px",
      backgroundColor: isSizeUnderlined
        ? "transparent"
        : sizeSelectorItem[type].backgroundColor,
      borderRadius: sizeSelectorItem[type].borderRadius,
      borderWidth,
      color: isSizeUnderlined ? "#000000" : sizeSelectorItem[type].fontColor,
      fontSize: sizeSelectorItem[type].fontSize,
      fontWeight: sizeSelectorItem[type]
        .fontWeight as React.CSSProperties["fontWeight"],
      textTransform: sizeSelectorItem[type]
        .textTransform as React.CSSProperties["textTransform"],
      border: `${borderWidth} solid ${sizeSelectorItem[type].borderColor}`,
      height: `calc(13px * 3)`,
      fontFamily,
      letterSpacing: (sizeSelectorItem[type] as any)?.letterSpacing,
    };
    return bodyItemsStyles;
  };

  const titlesStyles = {
    fontWeight: welcomeScreenUI.input_fields.title.fontWeight,
    textAlign: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["textAlign"],
    color: welcomeScreenUI.input_fields.title
      .fontColor as React.CSSProperties["color"],
    fontSize: isMobile
      ? "14px"
      : (welcomeScreenUI.input_fields.title
          .fontSize as React.CSSProperties["fontSize"]),
    justifyContent: welcomeScreenUI.input_fields.title
      .textAlign as React.CSSProperties["justifyContent"],
    fontStyle: (welcomeScreenUI.input_fields.title as any)
      .fontStyle as React.CSSProperties["fontStyle"],
    fontFamily: (welcomeScreenUI.input_fields.title as any)
      .fontFamily as React.CSSProperties["fontFamily"],
  };

  const continueButtonStyles = !selectedSize
    ? disabledStylesContinue
    : activeStylesContinue;

  const mergedButtonStyles: React.CSSProperties = {
    ...continueButtonStyles,
    ...(isIntersport && hovered && selectedSize ? hoverStylesContinue : {}),
    textTransform:
      titleStyles.textTransform as React.CSSProperties["textTransform"],
    marginBottom: "10px",
    position: "relative",
    padding: "16px",
    height: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && selectedSize ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && selectedSize ? 1 : 0,
    transform: hovered && !selectedSize ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  return (
    <div className="breasts">
      <div
        className="breasts__titles"
        style={{
          display: "flex",
        }}
      >
        <p className="breasts__titles__title" style={titlesStyles}>
          {t("sizeSystem")}
        </p>
        <div onClick={() => setOpen(!open)}>
          {brandDefined?.name === "SRP" ? (
            <StyledSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          ) : (
            <BaseSizeSelector
              sizeCountry={sizeCountry}
              setSizeCountry={setSizeCountry}
              open={open}
              setOpen={setOpen}
            />
          )}
        </div>
      </div>
      <div className="breasts__titles">
        <p className="breasts__titles__title" style={titlesStyles}>
          {t("sizeTitle")}
        </p>
      </div>
      <div className="breasts__body">
        <table className="breasts__body__table">
          <tbody>
            <tr>
              {configuredShoeSizes.find(
                (value: any) => value.name === sizeCountry
              )?.sizes.map((row: any) => {
                const selectedGender = gender === GENDERS.M ? "male" : "female";
                const value =
                  selectedGender === "male" ? row?.male : row?.female;

                return (
                  <td key={value}>
                    <button
                      className={`breasts__body__table__cell ${
                        selectedSize === value
                          ? "breasts__body__table__cell--selected"
                          : undefined
                      }`}
                      onClick={() => setSelectedSize(value)}
                      style={Object.assign({
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width:
                          value?.toString()?.length > 4
                            ? "auto"
                            : `calc(${
                                getBodyItemsStyles(
                                  selectedSize === value
                                    ? "focused"
                                    : "unfocused"
                                ).padding
                              } * 3)`,
                        ...(selectedSize === value
                          ? getBodyItemsStyles("focused")
                          : getBodyItemsStyles("unfocused")),
                      })}
                    >
                      {value}
                    </button>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="continue-button"
        onClick={() => {
          nextStep();
        }}
        style={mergedButtonStyles}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          {t("continue")}

          {isIntersport && (
            <span style={arrowContainerStyle} aria-hidden>
              <ChevronRight style={iconStyle} />
            </span>
          )}
        </div>
      </button>

      <MeasuredBy step={step} />
    </div>
  );
};

export default StepScanSelect;
